import { useEffect, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useLatest } from "hooks/useLatest";
import {
  HANDLE_HIT_SIZE_PX,
  HANDLE_SIZE_PX,
  HANDLE_STROKE_PX,
} from "./constants";
import type { BboxEditHandlesProps, EditDragState, Point } from "./types";

const IDLE: EditDragState = { phase: "idle" };

const clientPointOf = (event: ReactMouseEvent<SVGRectElement>): Point => ({
  x: event.clientX,
  y: event.clientY,
});

export const BboxEditHandles = ({
  bbox,
  inflatePdf,
  zoomLevel,
  onChange,
}: BboxEditHandlesProps) => {
  const latestOnChange = useLatest(onChange);
  const [dragState, setDragState] = useState<EditDragState>(IDLE);

  const handleSize = HANDLE_SIZE_PX / zoomLevel;
  const hitSize = HANDLE_HIT_SIZE_PX / zoomLevel;
  const corners = [
    { corner: { x: bbox.x1, y: bbox.y1 }, anchor: { x: bbox.x2, y: bbox.y2 } },
    { corner: { x: bbox.x2, y: bbox.y1 }, anchor: { x: bbox.x1, y: bbox.y2 } },
    { corner: { x: bbox.x1, y: bbox.y2 }, anchor: { x: bbox.x2, y: bbox.y1 } },
    { corner: { x: bbox.x2, y: bbox.y2 }, anchor: { x: bbox.x1, y: bbox.y1 } },
  ];

  const startMove = (event: ReactMouseEvent<SVGRectElement>) => {
    // The panel arms its pan on mousedown; the selected element is not something to pan by.
    event.stopPropagation();
    event.preventDefault();
    setDragState({
      phase: "moving",
      bboxStart: bbox,
      clientStart: clientPointOf(event),
    });
  };

  const startResize =
    (corner: Point, anchor: Point) =>
    (event: ReactMouseEvent<SVGRectElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setDragState({
        phase: "resizing",
        anchor,
        cornerStart: corner,
        clientStart: clientPointOf(event),
      });
    };

  useEffect(() => {
    if (dragState.phase === "idle") return;
    const { clientStart } = dragState;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = (event.clientX - clientStart.x) / zoomLevel;
      const deltaY = (event.clientY - clientStart.y) / zoomLevel;

      if (dragState.phase === "moving") {
        const { bboxStart } = dragState;
        latestOnChange.current({
          x1: bboxStart.x1 + deltaX,
          y1: bboxStart.y1 + deltaY,
          x2: bboxStart.x2 + deltaX,
          y2: bboxStart.y2 + deltaY,
        });
        return;
      }

      const { anchor, cornerStart } = dragState;
      const corner = { x: cornerStart.x + deltaX, y: cornerStart.y + deltaY };
      latestOnChange.current({
        x1: Math.min(anchor.x, corner.x),
        y1: Math.min(anchor.y, corner.y),
        x2: Math.max(anchor.x, corner.x),
        y2: Math.max(anchor.y, corner.y),
      });
    };
    const endDrag = () => setDragState(IDLE);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", endDrag);
    // A mouseup never reaches the page when the window loses focus mid-drag.
    window.addEventListener("blur", endDrag);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("blur", endDrag);
    };
  }, [dragState, zoomLevel, latestOnChange]);

  return (
    <g style={{ pointerEvents: "all" }}>
      <rect
        x={bbox.x1 - inflatePdf}
        y={bbox.y1 - inflatePdf}
        width={bbox.x2 - bbox.x1 + 2 * inflatePdf}
        height={bbox.y2 - bbox.y1 + 2 * inflatePdf}
        fill="transparent"
        style={{ cursor: "move" }}
        onMouseDown={startMove}
      />
      {corners.map(({ corner, anchor }, index) => {
        // Which way this corner faces, so the handle sits on the inflated outline.
        const signX = Math.sign(corner.x - anchor.x);
        const signY = Math.sign(corner.y - anchor.y);
        const centerX = corner.x + signX * inflatePdf;
        const centerY = corner.y + signY * inflatePdf;
        return (
          <g key={index}>
            <rect
              x={centerX - handleSize / 2}
              y={centerY - handleSize / 2}
              width={handleSize}
              height={handleSize}
              fill="#ffffff"
              stroke="#000000"
              strokeWidth={HANDLE_STROKE_PX / zoomLevel}
            />
            {/* Invisible, larger than the drawn square, so a grab near the corner still resizes. */}
            <rect
              x={centerX - hitSize / 2}
              y={centerY - hitSize / 2}
              width={hitSize}
              height={hitSize}
              fill="transparent"
              style={{
                cursor: signX === signY ? "nwse-resize" : "nesw-resize",
              }}
              onMouseDown={startResize(corner, anchor)}
            />
          </g>
        );
      })}
    </g>
  );
};
